import React from "react";
import "./AccountScreen.css";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useState, useEffect, useCallback, useMemo } from "react";
import { API_IP, STRIPE_KEY } from "../global_variables";
import NewCardModal from "./AccountComponents/NewCardModal";
import PromptModal from "./ReuseComponent/PromptModal";

const stripePromise = loadStripe(STRIPE_KEY);

const TABS = [
  { id: "overview", label: "Overview", icon: "👤" },
  { id: "payment", label: "Payment Methods", icon: "💳" },
  { id: "security", label: "Security", icon: "🔐" },
];

const CARD_BRAND_STYLES = {
  visa: { gradient: "linear-gradient(135deg, #1a1f71 0%, #3b5bd9 100%)", label: "VISA" },
  mastercard: { gradient: "linear-gradient(135deg, #eb001b 0%, #f79e1b 100%)", label: "MasterCard" },
  amex: { gradient: "linear-gradient(135deg, #2e77bc 0%, #6cb4e4 100%)", label: "AMEX" },
  discover: { gradient: "linear-gradient(135deg, #ff6000 0%, #ffb347 100%)", label: "Discover" },
  unionpay: { gradient: "linear-gradient(135deg, #e21836 0%, #00447c 50%, #007b3a 100%)", label: "UnionPay" },
  jcb: { gradient: "linear-gradient(135deg, #0e4c96 0%, #d8232a 50%, #1e8e3e 100%)", label: "JCB" },
  diners: { gradient: "linear-gradient(135deg, #004a97 0%, #0079be 100%)", label: "Diners" },
  default: { gradient: "linear-gradient(135deg, #1d4e89 0%, #6e8c6e 100%)", label: "CARD" },
};

const getCardStyle = (brand) => {
  if (!brand) return CARD_BRAND_STYLES.default;
  const key = brand.toLowerCase().replace(/\s/g, "");
  return CARD_BRAND_STYLES[key] || CARD_BRAND_STYLES.default;
};

const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: "", color: "#e0e0e0" };
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  const levels = [
    { label: "Too weak", color: "#c62828" },
    { label: "Weak", color: "#d97706" },
    { label: "Fair", color: "#b88a3e" },
    { label: "Good", color: "#8faf8f" },
    { label: "Strong", color: "#6e8c6e" },
    { label: "Excellent", color: "#5a7a5a" },
  ];
  return { score, ...levels[score] };
};

const formatDate = (date) => {
  try {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

const UserAccountScreen = ({ setSelectedSection }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [isNewCardModalVisible, setIsNewCardModalVisible] = useState(false);
  const [clientSecret, setClientSecret] = useState("");

  const [activeTab, setActiveTab] = useState("overview");
  const [defaultCardId, setDefaultCardId] = useState(null);
  const [cardToDelete, setCardToDelete] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingCard, setDeletingCard] = useState(false);
  const [cardActionError, setCardActionError] = useState(null);

  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [passwordMsg, setPasswordMsg] = useState(null);
  const [pwSubmitting, setPwSubmitting] = useState(false);

  const [accountInfo, setAccountInfo] = useState(null);

  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const EMPTY_ADDRESS = {
    line1: "",
    city: "",
    region: "",
    country: "",
    postal: "",
  };
  const [editingBilling, setEditingBilling] = useState(false);
  const [billingAddress, setBillingAddress] = useState(EMPTY_ADDRESS);
  const [billingDraft, setBillingDraft] = useState(EMPTY_ADDRESS);
  const [savingBilling, setSavingBilling] = useState(false);
  const [billingMsg, setBillingMsg] = useState(null);

  const storedUsername = localStorage.getItem("username") || "Guest";
  const username = accountInfo?.username || storedUsername;
  const userEmail = accountInfo?.email || "—";
  const phoneNumber = accountInfo?.phone_number
    ? String(accountInfo.phone_number)
    : "—";
  const lastLogin = useMemo(() => new Date().toISOString(), []);

  const toggleNewCardModal = () => setIsNewCardModalVisible(!isNewCardModalVisible);
  const toggleRefresh = () => setIsRefresh(!isRefresh);
  const toggleAlert = () => setIsAlertVisible(!isAlertVisible);

  const fetchClientSecret = useCallback(async () => {
    try {
      const bearerToken = localStorage.getItem("token");
      const response = await fetch(`${API_IP}/api/create-setup-intent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      const data = await response.json();
      if (data.clientSecret) {
        setClientSecret(data.clientSecret);
        setCards(data.listCards || []);
        if (data.listCards && data.listCards.length > 0 && !defaultCardId) {
          setDefaultCardId(data.listCards[0].id);
        }
        if (data.address) {
          const mapped = {
            line1: data.address.line1 || "",
            city: data.address.city || "",
            region: data.address.state || "",
            country: data.address.country || "",
            postal: data.address.postal_code || "",
          };
          setBillingAddress(mapped);
          setBillingDraft(mapped);
        }
        setLoading(false);
      } else {
        console.error("Failed to get client secret:", data.error);
      }
    } catch (error) {
      console.error("Error fetching client secret:", error);
    }
  }, [isRefresh, defaultCardId]);

  useEffect(() => {
    fetchClientSecret();
  }, [fetchClientSecret]);

  useEffect(() => {
    const fetchAccountInfo = async () => {
      try {
        const bearerToken = localStorage.getItem("token");
        if (!bearerToken) return;
        const response = await fetch(`${API_IP}/api/setting`, {
          method: "GET",
          headers: { Authorization: `Bearer ${bearerToken}` },
        });
        if (!response.ok) return;
        const data = await response.json();
        setAccountInfo(data);
      } catch (err) {
        console.error("Failed to load account info:", err);
      }
    };
    fetchAccountInfo();
  }, []);

  const strength = getPasswordStrength(newPw);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwSubmitting) return;
    if (!currentPw || !newPw || !confirmPw) {
      setPasswordMsg({ type: "error", text: "Please fill in all password fields." });
      return;
    }
    if (newPw !== confirmPw) {
      setPasswordMsg({ type: "error", text: "New password and confirmation do not match." });
      return;
    }
    if (strength.score < 3) {
      setPasswordMsg({ type: "error", text: "Please choose a stronger password." });
      return;
    }

    setPwSubmitting(true);
    setPasswordMsg(null);
    try {
      const bearerToken = localStorage.getItem("token");
      const response = await fetch(`${API_IP}/api/account/password`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
        body: JSON.stringify({ currentPassword: currentPw, newPassword: newPw }),
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (response.ok) {
        setPasswordMsg({
          type: "success",
          text: (data && data.message) || "Password updated successfully.",
        });
        setCurrentPw("");
        setNewPw("");
        setConfirmPw("");
        setShowCurrentPw(false);
        setShowNewPw(false);
      } else {
        const errText =
          (data && data.error) ||
          (response.status === 401
            ? "Current password is incorrect."
            : "Failed to update password. Please try again.");
        setPasswordMsg({ type: "error", text: errText });
      }
    } catch (err) {
      console.error("Change password error:", err);
      setPasswordMsg({
        type: "error",
        text: "Unable to reach the server. Please try again.",
      });
    } finally {
      setPwSubmitting(false);
    }
  };

  const requestDeleteCard = (card) => {
    setCardToDelete(card);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteCard = async () => {
    if (!cardToDelete || deletingCard) return;
    setDeletingCard(true);
    setCardActionError(null);
    try {
      const bearerToken = localStorage.getItem("token");
      const response = await fetch(
        `${API_IP}/api/payment-method/${encodeURIComponent(cardToDelete.id)}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${bearerToken}`,
          },
        },
      );

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        setCardActionError(
          (data && data.error) || "Failed to remove card. Please try again.",
        );
        return;
      }

      const removedId = cardToDelete.id;
      setCards((prev) => prev.filter((c) => c.id !== removedId));
      if (defaultCardId === removedId) {
        setDefaultCardId(null);
      }
      setCardToDelete(null);
      setDeleteConfirmOpen(false);
      toggleRefresh();
    } catch (err) {
      console.error("Remove card error:", err);
      setCardActionError("Unable to reach the server. Please try again.");
    } finally {
      setDeletingCard(false);
    }
  };

  const saveBillingAddress = async () => {
    if (savingBilling) return;
    const { line1, city, region, country, postal } = billingDraft;
    if (!line1 || !city || !region || !country || !postal) {
      setBillingMsg({
        type: "error",
        text: "Please fill in all billing address fields.",
      });
      return;
    }

    setSavingBilling(true);
    setBillingMsg(null);
    try {
      const bearerToken = localStorage.getItem("token");
      const response = await fetch(`${API_IP}/api/billing-address`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
        body: JSON.stringify(billingDraft),
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        setBillingMsg({
          type: "error",
          text:
            (data && data.error) ||
            "Failed to save billing address. Please try again.",
        });
        return;
      }

      const saved = data && data.address
        ? {
            line1: data.address.line1 || "",
            city: data.address.city || "",
            region: data.address.state || "",
            country: data.address.country || "",
            postal: data.address.postal_code || "",
          }
        : billingDraft;
      setBillingAddress(saved);
      setBillingDraft(saved);
      setEditingBilling(false);
      setBillingMsg({
        type: "success",
        text: (data && data.message) || "Billing address saved.",
      });
    } catch (err) {
      console.error("Save billing address error:", err);
      setBillingMsg({
        type: "error",
        text: "Unable to reach the server. Please try again.",
      });
    } finally {
      setSavingBilling(false);
    }
  };

  const cancelBillingEdit = () => {
    if (savingBilling) return;
    setBillingDraft(billingAddress);
    setBillingMsg(null);
    setEditingBilling(false);
  };

  const hasBillingAddress = Boolean(
    billingAddress.line1 ||
      billingAddress.city ||
      billingAddress.region ||
      billingAddress.country ||
      billingAddress.postal,
  );

  if (!clientSecret) {
    return (
      <div className="loading-container">
        <img src={require("../images/loading_icon.gif")} alt="Loading" />
      </div>
    );
  }

  const options = {
    appearance: { theme: "stripe" },
    clientSecret: clientSecret,
  };

  const initials = username
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <>
      <Elements stripe={stripePromise} options={options}>
        <NewCardModal
          showModal={isNewCardModalVisible}
          handleClose={toggleNewCardModal}
          toggleAlert={toggleAlert}
          toggleRefresh={toggleRefresh}
          clientSecret={clientSecret}
        />
        <PromptModal
          title={"Your card has been successfully saved"}
          type={"alert"}
          showModal={isAlertVisible}
          handleClose={toggleAlert}
        />
        <PromptModal
          title={`Remove card ending in ${cardToDelete?.card?.last4 || ""}?`}
          type={"binary"}
          showModal={deleteConfirmOpen}
          isLoading={deletingCard}
          handleClose={() => {
            if (deletingCard) return;
            setDeleteConfirmOpen(false);
            setCardToDelete(null);
            setCardActionError(null);
          }}
          handleConsent={confirmDeleteCard}
        />
      </Elements>

      <div className="screen-container account-screen">
        <div className="account-hero">
          <div className="account-hero-main">
            <div className="account-avatar">{initials || "U"}</div>
            <div className="account-hero-text">
              <h1 className="account-hero-title">
                Welcome back, <span>{username}</span>
              </h1>
              <p className="account-hero-subtitle">
                Manage your profile, payments, and security preferences all in one place.
              </p>
              <div className="account-hero-meta">
                <span className="account-meta-pill">
                  <span className="meta-dot meta-dot-green" /> Active account
                </span>
                <span className="account-meta-pill">✉ {userEmail}</span>
                {phoneNumber !== "—" && (
                  <span className="account-meta-pill">☎ {phoneNumber}</span>
                )}
              </div>
            </div>
          </div>
          <div className="account-hero-stats">
            <div className="stat-card">
              <div className="stat-value">{cards.length}</div>
              <div className="stat-label">Saved Cards</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{twoFactorEnabled ? "On" : "Off"}</div>
              <div className="stat-label">Two-Factor Auth</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {new Date(lastLogin).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </div>
              <div className="stat-label">Last Login</div>
            </div>
          </div>
        </div>

        <div className="account-tabs-modern">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              className={`account-tab-modern ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {activeTab === "overview" && (
          <div className="tab-panel">
            <div className="panel-grid">
              <section className="panel-card">
                <header className="panel-card-header">
                  <div>
                    <h2>Personal Information</h2>
                    <p>Your public profile information</p>
                  </div>
                  <button
                    className="ghost-button"
                    onClick={() => setSelectedSection && setSelectedSection("Profile")}
                  >
                    Edit Profile →
                  </button>
                </header>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Username</span>
                    <span className="info-value">{username}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Email</span>
                    <span className="info-value">{userEmail}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Phone</span>
                    <span className="info-value">{phoneNumber}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Account Status</span>
                    <span className="status-badge status-active">● Active</span>
                  </div>
                </div>
              </section>

              <section className="panel-card">
                <header className="panel-card-header">
                  <div>
                    <h2>Notification Preferences</h2>
                    <p>Choose how we contact you</p>
                  </div>
                </header>
                <div className="toggle-list">
                  <label className="toggle-row">
                    <div>
                      <div className="toggle-title">Email notifications</div>
                      <div className="toggle-sub">Booking confirmations, reminders, and receipts.</div>
                    </div>
                    <input
                      type="checkbox"
                      className="toggle-switch"
                      checked={emailNotifications}
                      onChange={(e) => setEmailNotifications(e.target.checked)}
                    />
                  </label>
                  <label className="toggle-row">
                    <div>
                      <div className="toggle-title">Marketing emails</div>
                      <div className="toggle-sub">News, product updates, and special offers.</div>
                    </div>
                    <input
                      type="checkbox"
                      className="toggle-switch"
                      checked={marketingOptIn}
                      onChange={(e) => setMarketingOptIn(e.target.checked)}
                    />
                  </label>
                </div>
              </section>
            </div>
          </div>
        )}

        {activeTab === "payment" && (
          <div className="tab-panel">
            <section className="panel-card">
              <header className="panel-card-header">
                <div>
                  <h2>Payment Methods</h2>
                  <p>Securely manage your saved cards</p>
                </div>
                <button className="primary-button" onClick={toggleNewCardModal}>
                  + Add New Card
                </button>
              </header>

              {cardActionError && (
                <div className="form-message error" style={{ marginBottom: 12 }}>
                  {cardActionError}
                </div>
              )}

              {cards.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">💳</div>
                  <h3>No cards saved yet</h3>
                  <p>Add a payment method to speed up checkout and booking.</p>
                  <button className="primary-button" onClick={toggleNewCardModal}>
                    Add Your First Card
                  </button>
                </div>
              ) : (
                <div className="cards-grid">
                  {cards.map((card) => {
                    const style = getCardStyle(card.card.brand);
                    const isDefault = defaultCardId === card.id;
                    return (
                      <div key={card.id} className={`credit-card ${isDefault ? "is-default" : ""}`}>
                        <div
                          className="credit-card-visual"
                          style={{ background: style.gradient }}
                        >
                          <div className="credit-card-top">
                            <div className="card-chip" />
                            {isDefault && <span className="default-badge">Default</span>}
                          </div>
                          <div className="credit-card-number">
                            •••• •••• •••• {card.card.last4}
                          </div>
                          <div className="credit-card-bottom">
                            <div>
                              <div className="cc-label">Expires</div>
                              <div className="cc-value">
                                {String(card.card.exp_month).padStart(2, "0")}/
                                {String(card.card.exp_year).slice(-2)}
                              </div>
                            </div>
                            <div className="cc-brand">{style.label}</div>
                          </div>
                        </div>
                        <div className="credit-card-actions">
                          {!isDefault && (
                            <button
                              className="link-button"
                              onClick={() => setDefaultCardId(card.id)}
                            >
                              Set as default
                            </button>
                          )}
                          {isDefault && <span className="muted-text">Primary payment method</span>}
                          <button
                            className="icon-button danger"
                            onClick={() => requestDeleteCard(card)}
                            aria-label="Remove card"
                          >
                            <img
                              className="icon-img"
                              src={require("../images/trash-icon.png")}
                              alt="Remove"
                            />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="panel-card">
              <header className="panel-card-header">
                <div>
                  <h2>Billing Address</h2>
                  <p>Used for invoices and receipts</p>
                </div>
                {!editingBilling && hasBillingAddress && (
                  <button
                    className="ghost-button"
                    onClick={() => {
                      setBillingMsg(null);
                      setEditingBilling(true);
                    }}
                  >
                    Edit
                  </button>
                )}
              </header>
              {billingMsg && !editingBilling && (
                <div
                  className={`form-message ${billingMsg.type}`}
                  style={{ marginBottom: 12 }}
                >
                  {billingMsg.text}
                </div>
              )}
              {!editingBilling ? (
                hasBillingAddress ? (
                  <div className="billing-address-display">
                    <p>{billingAddress.line1}</p>
                    <p>
                      {billingAddress.city}
                      {billingAddress.city && billingAddress.region ? ", " : ""}
                      {billingAddress.region}
                    </p>
                    <p>
                      {billingAddress.country} {billingAddress.postal}
                    </p>
                  </div>
                ) : (
                  <div className="empty-state">
                    <div className="empty-state-icon">📍</div>
                    <h3>No billing address saved</h3>
                    <p>Add a billing address for invoices and receipts.</p>
                    <button
                      className="primary-button"
                      onClick={() => {
                        setBillingMsg(null);
                        setEditingBilling(true);
                      }}
                    >
                      Add Billing Address
                    </button>
                  </div>
                )
              ) : (
                <div className="billing-form">
                  <div className="form-row">
                    <div className="form-field">
                      <label>Street Address</label>
                      <input
                        type="text"
                        value={billingDraft.line1}
                        onChange={(e) =>
                          setBillingDraft({ ...billingDraft, line1: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="form-row two-col">
                    <div className="form-field">
                      <label>City</label>
                      <input
                        type="text"
                        value={billingDraft.city}
                        onChange={(e) =>
                          setBillingDraft({ ...billingDraft, city: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-field">
                      <label>Region / State</label>
                      <input
                        type="text"
                        value={billingDraft.region}
                        onChange={(e) =>
                          setBillingDraft({ ...billingDraft, region: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="form-row two-col">
                    <div className="form-field">
                      <label>Country</label>
                      <input
                        type="text"
                        value={billingDraft.country}
                        onChange={(e) =>
                          setBillingDraft({ ...billingDraft, country: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-field">
                      <label>Postal Code</label>
                      <input
                        type="text"
                        value={billingDraft.postal}
                        onChange={(e) =>
                          setBillingDraft({ ...billingDraft, postal: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  {billingMsg && (
                    <div className={`form-message ${billingMsg.type}`}>
                      {billingMsg.text}
                    </div>
                  )}
                  <div className="form-actions">
                    <button
                      className="ghost-button"
                      onClick={cancelBillingEdit}
                      disabled={savingBilling}
                    >
                      Cancel
                    </button>
                    <button
                      className="primary-button"
                      onClick={saveBillingAddress}
                      disabled={savingBilling}
                    >
                      {savingBilling ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

        {activeTab === "security" && (
          <div className="tab-panel">
            <section className="panel-card">
              <header className="panel-card-header">
                <div>
                  <h2>Change Password</h2>
                  <p>Choose a strong, unique password to protect your account.</p>
                </div>
              </header>
              <form className="password-form" onSubmit={handleChangePassword}>
                <div className="form-field">
                  <label>Current Password</label>
                  <div className="input-with-action">
                    <input
                      type={showCurrentPw ? "text" : "password"}
                      value={currentPw}
                      onChange={(e) => setCurrentPw(e.target.value)}
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      className="input-action"
                      onClick={() => setShowCurrentPw(!showCurrentPw)}
                    >
                      {showCurrentPw ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
                <div className="form-row two-col">
                  <div className="form-field">
                    <label>New Password</label>
                    <div className="input-with-action">
                      <input
                        type={showNewPw ? "text" : "password"}
                        value={newPw}
                        onChange={(e) => setNewPw(e.target.value)}
                        placeholder="At least 8 characters"
                      />
                      <button
                        type="button"
                        className="input-action"
                        onClick={() => setShowNewPw(!showNewPw)}
                      >
                        {showNewPw ? "Hide" : "Show"}
                      </button>
                    </div>
                    {newPw && (
                      <div className="strength-meter">
                        <div className="strength-bar-track">
                          <div
                            className="strength-bar-fill"
                            style={{
                              width: `${(strength.score / 5) * 100}%`,
                              background: strength.color,
                            }}
                          />
                        </div>
                        <span className="strength-label" style={{ color: strength.color }}>
                          {strength.label}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="form-field">
                    <label>Confirm New Password</label>
                    <input
                      type={showNewPw ? "text" : "password"}
                      value={confirmPw}
                      onChange={(e) => setConfirmPw(e.target.value)}
                      placeholder="Re-enter new password"
                    />
                  </div>
                </div>
                {passwordMsg && (
                  <div className={`form-message ${passwordMsg.type}`}>{passwordMsg.text}</div>
                )}
                <div className="form-actions">
                  <button
                    type="submit"
                    className="primary-button"
                    disabled={pwSubmitting}
                  >
                    {pwSubmitting ? "Updating..." : "Update Password"}
                  </button>
                </div>
              </form>
            </section>

            {/* <section className="panel-card">
              <header className="panel-card-header">
                <div>
                  <h2>Two-Factor Authentication</h2>
                  <p>Add an extra layer of protection to your account.</p>
                </div>
              </header>
              <div className="security-row">
                <div>
                  <div className="toggle-title">
                    Authenticator App{" "}
                    {twoFactorEnabled ? (
                      <span className="status-badge status-active">Enabled</span>
                    ) : (
                      <span className="status-badge status-inactive">Disabled</span>
                    )}
                  </div>
                  <div className="toggle-sub">
                    Use an app like Google Authenticator or Authy to generate sign-in codes.
                  </div>
                </div>
                <button
                  className={twoFactorEnabled ? "ghost-button" : "primary-button"}
                  onClick={() => setTwoFactorEnabled(!twoFactorEnabled)}
                >
                  {twoFactorEnabled ? "Disable" : "Enable 2FA"}
                </button>
              </div>
            </section> */}

            <section className="panel-card">
              <header className="panel-card-header">
                <div>
                  <h2>Recent Activity</h2>
                  <p>Review recent sign-ins to your account.</p>
                </div>
              </header>
              <ul className="activity-list">
                <li className="activity-item">
                  <div className="activity-dot" />
                  <div className="activity-main">
                    <div className="activity-title">Current session · Web</div>
                    <div className="activity-sub">{formatDate(lastLogin)} · This device</div>
                  </div>
                  <span className="status-badge status-active">Active</span>
                </li>
                <li className="activity-item">
                  <div className="activity-dot inactive" />
                  <div className="activity-main">
                    <div className="activity-title">Sign-in · Mobile</div>
                    <div className="activity-sub">Yesterday · Victoria, BC</div>
                  </div>
                  <span className="muted-text">Expired</span>
                </li>
              </ul>
            </section>

            {/* <section className="panel-card danger-zone">
              <header className="panel-card-header">
                <div>
                  <h2>Danger Zone</h2>
                  <p>Irreversible actions that affect your account.</p>
                </div>
              </header>
              <div className="security-row">
                <div>
                  <div className="toggle-title">Delete account</div>
                  <div className="toggle-sub">
                    Permanently remove your account and all related data.
                  </div>
                </div>
                <button className="danger-button">Delete Account</button>
              </div>
            </section> */}
          </div>
        )}
      </div>
    </>
  );
};

export default UserAccountScreen;
